/**
 * Central-difference derivative of a series. Divides by the local sample
 * spacing, so results are in physical units (d/dt, d²/dt²).
 *
 * Order 1: interior uses the 3-point central form
 * `(y[i+1] - y[i-1]) / (t[i+1] - t[i-1])`, exact under any spacing. Endpoints
 * use a one-sided 2-point difference. Exact for non-uniform timestamps.
 *
 * Order 2: interior uses the 5-point stencil
 * `(-y[i-2] + 16 y[i-1] - 30 y[i] + 16 y[i+1] - y[i+2]) / (12 h²)` with local
 * mean spacing `h = (t[i+2] - t[i-2]) / 4`. The first two and last two samples
 * use a 3-point parabolic fit `(y[a] - 2 y[b] + y[c]) / h ^ 2`, `h = (t[2]-t[0])/2`
 * (or the tail equivalent). Both treat their window's mean interval as a single
 * `h`, so order 2 is exact only for uniform spacing (or small jitter around a
 * regular rate) — genuinely irregular timestamps yield an approximation.
 *
 * Short inputs (order 2): at n = 3 head and tail triplets coincide, so all
 * three outputs share one parabolic estimate. At n = 4 the interior loop is
 * skipped — the head fit sets indices 0,1 and the tail fit sets 2,3.
 *
 * `timeSeconds` must be strictly increasing; a zero or negative interval would
 * divide to `Infinity` or garbage. The expression evaluator's `deriv` registry
 * entry short-circuits short series before calling this; the `minLength` guard
 * below covers direct callers that bypass the registry.
 */
export function applyCentralDifference(
  values: number[],
  timeSeconds: number[],
  order: 1 | 2,
): number[] {
  if (values.length !== timeSeconds.length) {
    throw new Error(
      `applyCentralDifference: values and timeSeconds must have the same length (got ${values.length} and ${timeSeconds.length})`,
    );
  }
  if (order !== 1 && order !== 2) {
    throw new Error(
      `applyCentralDifference: order must be 1 or 2 (got ${String(order)})`,
    );
  }
  const minLength = order === 1 ? 2 : 3;
  if (values.length < minLength) {
    throw new Error(
      `applyCentralDifference: need at least ${minLength} samples for order ${order} (got ${values.length})`,
    );
  }

  for (let i = 1; i < timeSeconds.length; i++) {
    if (timeSeconds[i] <= timeSeconds[i - 1]) {
      throw new Error(
        `applyCentralDifference: timeSeconds must be strictly increasing (duplicate or out-of-order value at index ${i}: ${timeSeconds[i - 1]} >= ${timeSeconds[i]})`,
      );
    }
  }

  return order === 1
    ? firstDerivative(values, timeSeconds)
    : secondDerivative(values, timeSeconds);
}

function firstDerivative(y: number[], t: number[]): number[] {
  const n = y.length;
  const out: number[] = Array.from({ length: n });
  out[0] = (y[1] - y[0]) / (t[1] - t[0]);
  out[n - 1] = (y[n - 1] - y[n - 2]) / (t[n - 1] - t[n - 2]);
  for (let i = 1; i < n - 1; i++) {
    out[i] = (y[i + 1] - y[i - 1]) / (t[i + 1] - t[i - 1]);
  }
  return out;
}

function secondDerivative(y: number[], t: number[]): number[] {
  const n = y.length;
  const out: number[] = Array.from({ length: n });

  // Boundary: 3-point parabolic fit against the nearest triplet, local mean
  // spacing as h. Fills indices 0, 1, n-2, n-1 — the whole array when n is 3
  // or 4. Overlapping writes (e.g. index 1 at n = 3) repeat the same value.
  const headSpacing = (t[2] - t[0]) / 2;
  const headSecond = (y[0] - 2 * y[1] + y[2]) / (headSpacing * headSpacing);
  out[0] = headSecond;
  out[1] = headSecond;

  const tailSpacing = (t[n - 1] - t[n - 3]) / 2;
  const tailSecond =
    (y[n - 3] - 2 * y[n - 2] + y[n - 1]) / (tailSpacing * tailSpacing);
  out[n - 2] = tailSecond;
  out[n - 1] = tailSecond;

  // Interior: 5-point stencil with local mean spacing.
  for (let i = 2; i < n - 2; i++) {
    const h = (t[i + 2] - t[i - 2]) / 4;
    out[i] =
      (-y[i - 2] + 16 * y[i - 1] - 30 * y[i] + 16 * y[i + 1] - y[i + 2]) /
      (12 * h * h);
  }
  return out;
}
